/**
 * Servicio centralizado para llamadas API con autenticación
 */

// Configuración de la API
const API_KEY = process.env.REACT_APP_API_KEY || 'municipal-dashboard-2024-secure-key';

// URL base de la API - usar directamente la URL completa por ahora
const API_BASE_URL = 'https://municipal-dashboard-api.repasador-alpargata.workers.dev';

console.log('🔑 API Key cargada:', API_KEY ? 'SÍ' : 'NO', '(primeros 10 chars):', API_KEY?.substring(0, 10));
console.log('🌐 API Base URL:', API_BASE_URL);

/**
 * Construir URL completa para la API
 * @param {string} endpoint - Endpoint relativo (ej: '/api/proveedores')
 * @returns {string} - URL completa
 */
const buildApiUrl = (endpoint) => {
  // Si endpoint ya es una URL completa, devolverla tal como está
  if (endpoint.startsWith('http')) return endpoint;
  
  // Construir URL completa
  return `${API_BASE_URL}${endpoint}`;
};

// Headers por defecto para todas las requests
const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-API-Key': API_KEY,
  'User-Agent': 'Municipal-Dashboard-Chrome-Compatible'
});

/**
 * Función wrapper para fetch con autenticación automática
 * @param {string} url - URL del endpoint
 * @param {object} options - Opciones de fetch
 * @returns {Promise<Response>} - Response de fetch
 */
export const authenticatedFetch = async (url, options = {}) => {
  // Construir URL completa
  const fullUrl = buildApiUrl(url);
  
  const defaultOptions = {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-cache',
    headers: getDefaultHeaders(),
    ...options,
  };

  // Mergear headers si ya existen
  if (options.headers) {
    defaultOptions.headers = {
      ...getDefaultHeaders(),
      ...options.headers,
    };
  }

  console.log('🔐 Haciendo request autenticado a:', fullUrl);
  
  try {
    const response = await fetch(fullUrl, defaultOptions);
    
    // Log de respuesta para debugging
    if (!response.ok) {
      console.error('❌ Error en API:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText
      });
    } else {
      console.log('✅ Request exitoso a:', fullUrl);
    }
    
    return response;
  } catch (error) {
    console.error('❌ Error de red:', error);
    throw error;
  }
};

/**
 * Función helper para GET requests
 * @param {string} url - URL del endpoint
 * @returns {Promise<any>} - JSON response parseado
 */
export const apiGet = async (url) => {
  const response = await authenticatedFetch(url);
  
  if (!response.ok) {
    // Intentar leer el texto de error
    let errorText;
    try {
      errorText = await response.text();
      console.error('❌ Error response text:', errorText);
    } catch (e) {
      errorText = `HTTP ${response.status}`;
    }
    throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
  }
  
  // Verificar que la respuesta sea JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const responseText = await response.text();
    console.error('❌ Respuesta no es JSON:', responseText);
    throw new Error(`Respuesta no es JSON. Content-Type: ${contentType}, Body: ${responseText}`);
  }
  
  try {
    const result = await response.json();
    return result;
  } catch (parseError) {
    const responseText = await response.text();
    console.error('❌ Error parseando JSON:', parseError, 'Response:', responseText);
    throw new Error(`Error parseando JSON: ${parseError.message}. Response: ${responseText}`);
  }
};

/**
 * Función helper para POST requests
 * @param {string} url - URL del endpoint
 * @param {object} data - Datos a enviar
 * @returns {Promise<any>} - JSON response parseado
 */
export const apiPost = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Función helper para PUT requests
 * @param {string} url - URL del endpoint
 * @param {object} data - Datos a enviar
 * @returns {Promise<any>} - JSON response parseado
 */
export const apiPut = async (url, data) => {
  const response = await authenticatedFetch(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Función helper para DELETE requests
 * @param {string} url - URL del endpoint
 * @returns {Promise<any>} - JSON response parseado
 */
export const apiDelete = async (url) => {
  const response = await authenticatedFetch(url, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

export default {
  authenticatedFetch,
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
};